import { memo } from 'react';

import { Button, Group, Table, Text, Tooltip } from '@mantine/core';

import { SCUD_EMPTY } from '@/shared/constant/scudMapping';
import { IScudMapping } from '@/shared/types/scudMapping.types';

import { ScudStatusBadge } from './ScudStatusBadge';
import { CellLines } from './ScudTable';
import { formatScudDate, formatScudDateTime, joinParts, scudPassNumber } from './utils';

type ScudMappingRowProps = {
	mapping: IScudMapping;
	/** Причина с пометкой «(не используется)», если её сняли с использования */
	reasonLabel: string | null;
	number: number;
	isConfirming: boolean;
	/** В режиме «Все» неподтверждённые смешаны с обработанными — отмечаем, где нужно действие */
	isPendingMarked?: boolean;
	onConfirm: (mapping: IScudMapping) => void;
	onEdit: (id: number) => void;
};

const TABULAR = { fontVariantNumeric: 'tabular-nums' } as const;

export const ScudMappingRow = memo(function ScudMappingRow({
	mapping,
	reasonLabel,
	number,
	isConfirming,
	isPendingMarked,
	onConfirm,
	onEdit,
}: ScudMappingRowProps) {
	// «Подтвердить» в строке — только для неподтверждённой 1 (ТЗ 8.2, кол. 11); 3 подтверждается в форме
	const canConfirm = mapping.can_process && !mapping.guard_confirmed && mapping.match_status === 1;

	const processLabel = mapping.guard_confirmed ? 'Исправить' : 'Изменить';
	const editLabel = mapping.can_process ? processLabel : 'Открыть';

	return (
		<Table.Tr>
			<Table.Td
				style={
					isPendingMarked ? { boxShadow: 'inset 3px 0 0 var(--mantine-color-orange-5)' } : undefined
				}
			>
				<Text size="sm" c="dimmed" style={TABULAR}>
					{number}
				</Text>
			</Table.Td>
			<Table.Td>
				<Text size="sm">{mapping.location ?? SCUD_EMPTY}</Text>
			</Table.Td>
			<Table.Td>
				<Text size="sm">{mapping.skud_company ?? SCUD_EMPTY}</Text>
			</Table.Td>
			<Table.Td>
				<Text size="sm" style={TABULAR}>
					{scudPassNumber(mapping)}
				</Text>
			</Table.Td>
			<Table.Td>
				<CellLines
					main={mapping.full_name_skud}
					sub={joinParts(mapping.skud_department, mapping.skud_post)}
				/>
			</Table.Td>
			<Table.Td>
				<ScudStatusBadge status={mapping.match_status} />
			</Table.Td>
			<Table.Td>
				{mapping.match_status === 1 ? (
					<CellLines
						main={mapping.full_name_federation}
						sub={joinParts(
							mapping.federation?.company_name,
							mapping.federation?.unit_name,
							mapping.federation?.position,
						)}
					/>
				) : (
					<Text size="sm" c="dimmed">
						{mapping.match_status === 2 ? 'Несколько кандидатов' : SCUD_EMPTY}
					</Text>
				)}
			</Table.Td>
			<Table.Td style={{ whiteSpace: 'nowrap' }}>
				<Text size="sm" style={TABULAR}>
					{formatScudDate(mapping.last_pass)}
				</Text>
			</Table.Td>
			<Table.Td maw={220}>
				<Text size="sm">{reasonLabel ?? SCUD_EMPTY}</Text>
				{mapping.guard_comment && (
					<Tooltip label={mapping.guard_comment} multiline w={320} openDelay={300}>
						<Text size="xs" c="dimmed" lineClamp={2}>
							{mapping.guard_comment}
						</Text>
					</Tooltip>
				)}
			</Table.Td>
			<Table.Td style={{ whiteSpace: 'nowrap' }}>
				{mapping.guard_confirmed ? (
					<CellLines main={mapping.confirmed_by} sub={formatScudDateTime(mapping.confirmed_at)} />
				) : (
					<Text size="sm">{SCUD_EMPTY}</Text>
				)}
			</Table.Td>
			<Table.Td style={{ width: '1%', whiteSpace: 'nowrap' }}>
				<Group gap={6} wrap="nowrap" justify="flex-end">
					{canConfirm && (
						<Button
							size="compact-sm"
							radius="sm"
							loading={isConfirming}
							onClick={() => onConfirm(mapping)}
						>
							Подтвердить
						</Button>
					)}
					<Button
						size="compact-sm"
						radius="sm"
						variant="outline"
						onClick={() => onEdit(mapping.id)}
					>
						{editLabel}
					</Button>
				</Group>
			</Table.Td>
		</Table.Tr>
	);
});
